// Shell do seller (`spec/17-frontend.md` §8.1). Desenho do `AppShell` do croqui
// (`spec/24` §7): sidebar de 276px COLADA à esquerda (`bg-panel`, borda à direita, altura
// total), mascote + wordmark, `RevenueCard`, grupos e `ProfileCard` no rodapé. O
// cabeçalho de página (com o `ModeToggle`) é o `PageHeader` do kit, de cada tela.
//
// O layout checa **só** `get-session` (a única rota que não passa por `auth-session`) pra
// decidir se RENDERIZA o shell: checar `/v1/me` aqui pra essa decisão faria `/app/terms`
// se redirecionar para si mesma para sempre. Gates de domínio (`terms_not_accepted`,
// `mfa_required`) chegam como `403` de QUALQUER chamada guardada e são roteados pelo
// `QueryCache` global (`app/guards.tsx`, `handleGateError`), não por este componente.
//
// `MonthRevenue`/badges (WEB-50B) chamam rotas com esse mesmo gate de termos (`GET
// /v1/me`, `GET /v1/me/counters`): em `/app/terms` elas tomam `403 terms_not_accepted` e
// o guard global navega de volta pra cá, sem laço, porque é a MESMA rota (navigate
// idempotente), testado em `e2e/shell.spec.ts`.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createFileRoute,
  Navigate,
  Outlet,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { api } from '../api/client.ts';
import { profileQueryOptions } from '../api/queries/overview.ts';
import { summaryQueryOptions } from '../api/queries/reports.ts';
import { countersQueryOptions, sellerBadges } from '../api/queries/shell.ts';
import { sessionQueryOptions } from '../api/session.ts';
import { useLiveEvents } from '../app/live-events.ts';
import { useMode } from '../app/mode.tsx';
import { sellerNav } from '../app/nav-seller.ts';
import { NavDrawer } from '../components/app/nav-drawer.tsx';
import { CardSkeleton } from '../components/app/skeletons.tsx';
import { ProfileCard } from '../components/kit/profile-card.tsx';
import { RevenueCard } from '../components/kit/revenue-card.tsx';
import { Sidebar } from '../components/kit/sidebar.tsx';
import { todayIsoDate } from '../lib/dates.ts';
import { formatCents } from '../lib/money.ts';

/** Primeiro dia do mês corrente em `America/Sao_Paulo`, como `YYYY-MM-DD`. */
function monthToDate(): { from: string; to: string } {
  const to = todayIsoDate();
  return { from: `${to.slice(0, 7)}-01`, to };
}

/**
 * "Recebido no mês" do croqui (`spec/24` §3): `sales.gross_cents` do summary do mês
 * corrente. A META (`revenue_goal_cents` de `GET /v1/me`) liga aqui (WEB-50B, "Questões
 * abertas" 6 da WEB-50): sem meta cadastrada a barra some e o card mostra só o valor.
 */
function MonthRevenue() {
  const { mode } = useMode();
  const summary = useQuery(summaryQueryOptions(mode, monthToDate()));
  const profile = useQuery(profileQueryOptions(mode));
  const receivedCents = summary.data?.sales.gross_cents ?? 0;
  const goalCents = profile.data?.revenue_goal_cents ?? null;
  return (
    <RevenueCard
      value={formatCents(receivedCents)}
      fill={goalCents ? (receivedCents / goalCents) * 100 : 0}
      {...(goalCents === null ? {} : { goal: formatCents(goalCents) })}
    />
  );
}

function SellerProfile({ name, email }: { name: string; email: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const signOut = useMutation({
    mutationFn: async () => api('/v1/me/auth/sign-out', { method: 'POST' }),
    onSettled: () => {
      queryClient.clear();
      void navigate({ to: '/login' });
    },
  });
  return (
    <ProfileCard
      name={name}
      email={email}
      onLogout={() => {
        signOut.mutate();
      }}
    />
  );
}

/**
 * O croqui não tem este selo. Ele fica porque é dado real que o croqui omite
 * (`spec/24` §3, regra geral): enquanto o SSE não é alcançável, é o que explica por que a
 * tela atualiza por refetch.
 */
function LiveChip({ status }: { status: string }) {
  return (
    <span
      className="text-11 text-faint"
      title={
        status === 'unavailable'
          ? 'O stream de eventos não está alcançável; a tela atualiza por refetch.'
          : undefined
      }
    >
      {status === 'connected' ? 'ao vivo' : 'ao vivo indisponível'}
    </span>
  );
}

function SellerShell() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const session = useQuery(sessionQueryOptions);
  const { mode } = useMode();
  const live = useLiveEvents();
  // `enabled`: sem sessão de seller a chamada só daria 401. Erro aqui não quebra o shell
  // (item 4 da task): `counters.data` fica `undefined` e a `Sidebar` sobe sem badge, sem
  // toast.
  const counters = useQuery({
    ...countersQueryOptions(mode),
    enabled: session.data?.role === 'seller_owner',
  });
  const badges = counters.data === undefined ? undefined : sellerBadges(counters.data);

  if (session.isPending) {
    return (
      <div className="min-h-dvh p-4 sm:p-8">
        <CardSkeleton />
      </div>
    );
  }
  if (session.data === null || session.data === undefined) {
    return <Navigate to="/login" search={{ next: pathname }} replace />;
  }
  if (session.data.role !== 'seller_owner') {
    return <Navigate to="/no-access" replace />;
  }

  const user = session.data;

  return (
    <div className="flex min-h-dvh items-stretch">
      <Sidebar
        groups={sellerNav}
        activePath={pathname}
        revenue={<MonthRevenue />}
        {...(badges === undefined ? {} : { badges })}
        footer={
          <>
            <div className="mt-4 px-1">
              <LiveChip status={live.status} />
            </div>
            <SellerProfile name={user.name} email={user.email} />
          </>
        }
        className="sticky top-0 hidden h-dvh lg:flex"
      />

      <main className="flex min-w-0 flex-1 flex-col gap-5 p-4 sm:py-8 sm:pr-8 sm:pl-6">
        {/* Em 390px a sidebar vira gaveta atrás do botão de menu (`spec/24` §4 item 1). */}
        <div className="flex items-center gap-3 lg:hidden">
          <NavDrawer
            groups={sellerNav}
            activePath={pathname}
            footer={
              <>
                <MonthRevenue />
                <div className="mt-4 px-1">
                  <LiveChip status={live.status} />
                </div>
                <SellerProfile name={user.name} email={user.email} />
              </>
            }
          />
        </div>

        {mode === 'test' && (
          <div className="rounded-card border border-gold/40 bg-gold/10 px-4 py-2 text-11 text-gold">
            Modo de teste: nada aqui movimenta dinheiro de verdade.
          </div>
        )}

        <Outlet />
      </main>
    </div>
  );
}

export const Route = createFileRoute('/app')({ component: SellerShell });
